/**
 * Cross-customer reporting over a date range: billing, tickets, the fleet, time, and what the price
 * list actually sells - with PDF and CSV exports.
 *
 * The port of `Views/ReportsPage.xaml` + `ReportsViewModel`. The server does all the aggregation in
 * one payload (`ReportBuilder`), so this page only picks a range and lays the answer out. Two things
 * the DTO says about itself are repeated where a reader needs them: fleet counts and current unbilled
 * work are SNAPSHOTS that ignore the range, and the catalog's per-customer table covers every parts
 * charge while its per-item table covers only what came off the price list.
 *
 * The tables are `ReportTable`s, which stack their rows on a phone rather than panning the page
 * sideways the way the XAML's fixed columns did.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import type {
  CatalogCustomerRow,
  CatalogIdleItemRow,
  CatalogItemSalesRow,
  CatalogKindRow,
  CustomerDeviceRow,
  CustomerRevenueRow,
  CustomerTicketRow,
  CustomerTimeRow,
  OsBreakdownRow,
  ReportSummaryDto,
  UserTimeRow
} from '../contracts.gen.js';
import { download, openInNewTab } from '../download.js';
import { join, localDate, money } from '../format.js';
import { todayIso } from '../recurrence.js';
import { dateBox } from '../ui/DateBox.js';
import { ReportTable } from '../ui/ReportTable.js';
import { stat, type Stat } from '../ui/Stat.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  stackOf,
  text,
  vstack,
  type CrmLabel
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

/** `yyyy-MM-dd` `days` before today, in the reader's calendar. */
function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local midnight of a day as an instant, plus `days` for the exclusive upper bound. */
function localMidnight(iso: string, days = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days).toISOString() : undefined;
}

function hoursOf(minutes: number): string {
  return (Math.round((minutes / 60) * 10) / 10).toString();
}

function pct(value: number): string {
  return `${(Math.round(value * 10) / 10).toString()}%`;
}

export function reportsPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const status = new StatusLine();

      // =================================================================
      //  Range and exports
      // =================================================================

      const from = dateBox({ iso: daysAgoIso(30) });
      const to = dateBox({ iso: todayIso() });
      const rangeLine = muted('');

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Reports'),
        rangeLine,
        stackOf(
          buttonRow({ spacing: GAP.md, align: 'end' }),
          field('From', from.picker, { width: 150 }),
          field('To', to.picker, { width: 150 }),
          button('Refresh', () => void load(), true)
        ),
        stackOf(
          buttonRow(),
          button('Last 30 days', () => void setRange(daysAgoIso(30), todayIso())),
          button('Last 90 days', () => void setRange(daysAgoIso(90), todayIso())),
          button('This year', () => void setRange(`${new Date().getFullYear()}-01-01`, todayIso())),
          button('All time', () => void setRange('', ''))
        ),
        stackOf(buttonRow(), button('PDF', () => exportPdf()), button('CSV', () => void exportCsv())),
        status.label
      );

      // =================================================================
      //  Sections
      // =================================================================

      const sections = vstack({ spacing: GAP.lg, visible: false });

      function section(title: string, ...children: Parameters<typeof stackOf>[1][]) {
        const shell = card();
        shell.controls.add(stackOf(vstack({ spacing: GAP.md }), h2(title), ...children));
        sections.controls.add(shell);
      }

      function strip(...stats: Stat[]) {
        // Five 128px stats and their gaps fit one line of a desktop card; a phone wraps them.
        return stackOf(buttonRow({ spacing: GAP.lg, lineSpacing: GAP.md, align: 'start' }), ...stats.map(s => s.panel));
      }

      function subheading(label: string): CrmLabel {
        return muted(label, 'subtle');
      }

      // ---- billing ----
      const invoiced = stat('Invoiced');
      const paid = stat('Paid');
      const outstanding = stat('Outstanding');
      const unbilled = stat('Unbilled (now)');
      const invoiceCounts = text('');
      const revenueTable = new ReportTable<CustomerRevenueRow>({
        header: 'Customer',
        name: r => r.customerName,
        columns: [
          { header: 'Invoiced', width: 100, cell: r => money(r.invoiced) },
          { header: 'Paid', width: 100, cell: r => money(r.paid) },
          { header: 'Outstanding', width: 100, cell: r => money(r.outstanding) },
          { header: 'Unbilled', width: 100, cell: r => money(r.unbilled) }
        ],
        empty: 'Nothing billed in this range.'
      });
      section('Billing / Revenue', strip(invoiced, paid, outstanding, unbilled), invoiceCounts, revenueTable.panel);

      // ---- tickets ----
      const tTotal = stat('Total', 128);
      const tOpen = stat('Open', 128);
      const tResolved = stat('Resolved', 128);
      const tClosed = stat('Closed', 128);
      const tAvg = stat('Avg resolution', 128);
      const ticketMix = text('');
      const ticketTable = new ReportTable<CustomerTicketRow>({
        header: 'Customer',
        name: r => r.customerName,
        columns: [
          { header: 'Open', width: 80, cell: r => String(r.openCount) },
          { header: 'Total', width: 80, cell: r => String(r.totalCount) }
        ],
        empty: 'No tickets in this range.'
      });
      section('Tickets', strip(tTotal, tOpen, tResolved, tClosed, tAvg), ticketMix, ticketTable.panel);

      // ---- fleet ----
      const dManaged = stat('Managed', 128);
      const dOnline = stat('Online', 128);
      const dPending = stat('Pending', 128);
      const dArchived = stat('Archived', 128);
      const dMonitors = stat('Monitors', 128);
      const osTable = new ReportTable<OsBreakdownRow>({
        header: 'Operating system',
        name: r => r.os || 'Unknown',
        columns: [{ header: 'Devices', width: 80, cell: r => String(r.count) }]
      });
      const fleetTable = new ReportTable<CustomerDeviceRow>({
        header: 'Customer',
        name: r => r.customerName,
        columns: [
          { header: 'Devices', width: 80, cell: r => String(r.deviceCount) },
          { header: 'Online', width: 80, cell: r => String(r.onlineCount) }
        ]
      });
      section(
        'Devices / Fleet',
        muted('A snapshot of the fleet now - these counts ignore the date range.'),
        strip(dManaged, dOnline, dPending, dArchived, dMonitors),
        subheading('By operating system'),
        osTable.panel,
        subheading('By customer'),
        fleetTable.panel
      );

      // ---- time ----
      const billableHours = stat('Billable hours');
      const nonBillableHours = stat('Non-billable hours');
      const billableValue = stat('Billable value');
      const userTable = new ReportTable<UserTimeRow>({
        header: 'User',
        name: r => r.userName,
        columns: [
          { header: 'Hours', width: 80, cell: r => hoursOf(r.billableMinutes) },
          { header: 'Value', width: 110, cell: r => money(r.billableValue) }
        ],
        empty: 'No billable time in this range.'
      });
      const timeTable = new ReportTable<CustomerTimeRow>({
        header: 'Customer',
        name: r => r.customerName,
        columns: [
          { header: 'Hours', width: 80, cell: r => hoursOf(r.billableMinutes) },
          { header: 'Value', width: 110, cell: r => money(r.billableValue) }
        ]
      });
      section(
        'Time Tracking',
        strip(billableHours, nonBillableHours, billableValue),
        subheading('By user'),
        userTable.panel,
        subheading('By customer'),
        timeTable.panel
      );

      // ---- catalog ----
      const cCharges = stat('Charges', 128);
      const cCost = stat('Cost', 128);
      const cRevenue = stat('Revenue', 128);
      const cMargin = stat('Margin', 128);
      const cMarginPct = stat('Margin %', 128);
      const priceList = text('');
      const absorbed = text('');
      const kindTable = new ReportTable<CatalogKindRow>({
        header: 'Kind',
        name: r => r.kindName,
        columns: [
          { header: 'Items', width: 70, cell: r => String(r.itemCount) },
          { header: 'Charges', width: 80, cell: r => String(r.chargeCount) },
          { header: 'Revenue', width: 100, cell: r => money(r.revenue) },
          { header: 'Margin', width: 100, cell: r => money(r.margin), role: r => (r.margin < 0 ? 'danger' : 'text') }
        ]
      });
      const itemTable = new ReportTable<CatalogItemSalesRow>({
        header: 'Item',
        name: r => r.name,
        detail: r => join([r.sku, r.active ? null : 'retired', r.customerCount > 1 ? `${r.customerCount} customers` : null]),
        columns: [
          { header: 'Charges', width: 70, cell: r => String(r.chargeCount) },
          { header: 'Qty', width: 55, cell: r => String(r.quantity) },
          { header: 'Cost', width: 90, cell: r => money(r.cost) },
          { header: 'Revenue', width: 90, cell: r => money(r.revenue) },
          { header: 'Margin', width: 90, cell: r => money(r.margin), role: r => (r.margin < 0 ? 'danger' : 'text') },
          { header: 'Margin %', width: 75, cell: r => pct(r.marginPercent) },
          { header: 'Last charged', width: 100, cell: r => localDate(r.lastChargedUtc) }
        ],
        empty: 'Nothing off the price list was charged in this range.'
      });
      const catalogCustomerTable = new ReportTable<CatalogCustomerRow>({
        header: 'Customer',
        name: r => r.customerName,
        columns: [
          { header: 'Charges', width: 80, cell: r => String(r.chargeCount) },
          { header: 'Cost', width: 95, cell: r => money(r.cost) },
          { header: 'Revenue', width: 95, cell: r => money(r.revenue) },
          { header: 'Margin', width: 95, cell: r => money(r.margin), role: r => (r.margin < 0 ? 'danger' : 'text') }
        ]
      });
      const idleHeading = subheading('Not charged in this range');
      const idleTable = new ReportTable<CatalogIdleItemRow>({
        header: 'Item',
        name: r => r.name,
        detail: r => r.sku,
        columns: [
          { header: 'Kind', width: 90, cell: r => r.kindName },
          { header: 'Cost', width: 90, cell: r => money(r.unitCost) },
          // "Never" and "not this period" are different facts; the server ignores the range here.
          { header: 'Last charged', width: 110, cell: r => (r.lastChargedUtc ? localDate(r.lastChargedUtc) : 'never') }
        ]
      });
      section(
        'Catalog - Purchase History',
        strip(cCharges, cCost, cRevenue, cMargin, cMarginPct),
        priceList,
        absorbed,
        subheading('By kind'),
        kindTable.panel,
        subheading('By item - charges priced from the price list'),
        itemTable.panel,
        subheading('By customer - every parts charge, on the list or not'),
        catalogCustomerTable.panel,
        idleHeading,
        idleTable.panel
      );

      const { view, content } = scrollPage();
      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), header, sections));
      host.controls.add(view);

      // =================================================================
      //  Rendering
      // =================================================================

      function render(r: ReportSummaryDto): void {
        const b = r.billing;
        invoiced.set(money(b.invoicedTotal));
        paid.set(money(b.paidTotal));
        outstanding.set(money(b.outstandingTotal), '', b.outstandingTotal > 0 ? 'warn' : 'text');
        unbilled.set(money(b.unbilledValue), 'a snapshot, not the range');
        invoiceCounts.text = `Invoices: ${b.draftCount} draft · ${b.issuedCount} issued · ${b.paidCount} paid · ${b.voidCount} void`;
        revenueTable.set(b.byCustomer);

        const t = r.tickets;
        tTotal.set(String(t.total));
        tOpen.set(String(t.open + t.inProgress + t.waiting), join([`${t.inProgress} in progress`, `${t.waiting} waiting`]));
        tResolved.set(String(t.resolved));
        tClosed.set(String(t.closed));
        tAvg.set(`${hoursOf(t.avgResolutionHours * 60)} h`);
        ticketMix.text = `Priority: ${t.low} low · ${t.normal} normal · ${t.high} high · ${t.urgent} urgent    Source: ${t.fromAgent} agent · ${t.fromManual} staff`;
        ticketTable.set(t.byCustomer);

        const d = r.devices;
        dManaged.set(String(d.totalManaged));
        dOnline.set(String(d.online), `${d.offline} offline`);
        dPending.set(String(d.pendingApproval), '', d.pendingApproval > 0 ? 'warn' : 'text');
        dArchived.set(String(d.archived));
        dMonitors.set(String(d.monitors));
        osTable.set(d.byOs);
        fleetTable.set(d.byCustomer);

        const tm = r.time;
        billableHours.set(hoursOf(tm.billableMinutes));
        nonBillableHours.set(hoursOf(tm.nonBillableMinutes));
        billableValue.set(money(tm.billableValue));
        userTable.set(tm.byUser);
        timeTable.set(tm.byCustomer);

        const c = r.catalog;
        cCharges.set(String(c.chargeCount), `qty ${c.quantity}`);
        cCost.set(money(c.costTotal));
        cRevenue.set(money(c.revenueTotal));
        cMargin.set(money(c.marginTotal), '', c.marginTotal < 0 ? 'danger' : 'text');
        cMarginPct.set(pct(c.marginPercent));
        priceList.text = `Price list: ${c.activeItems} active · ${c.retiredItems} retired · ${c.contractPrices} contract prices`;
        absorbed.text =
          `Absorbed (non-billable) cost: ${money(c.absorbedCost)}    ` +
          `Off-catalog: ${c.offCatalogCharges} charges billing ${money(c.offCatalogRevenue)}`;
        kindTable.set(c.byKind);
        itemTable.set(c.byItem);
        catalogCustomerTable.set(c.byCustomer);
        // Hidden when every item sold, so a healthy catalog doesn't carry an empty heading.
        idleHeading.visible = c.notCharged.length > 0;
        idleTable.set(c.notCharged);

        sections.visible = true;
        // From the sections panel, not from `content`: the stack holding it did not change size, so a
        // pass starting above it never re-arranged it and the newly shown panel kept a stale width.
        relayout(sections);
      }

      function rangeLabel(): string {
        return `${from.iso() || 'the beginning'} to ${to.iso() || 'now'}`;
      }

      function bounds(): { fromUtc?: string; toUtc?: string } {
        return { fromUtc: localMidnight(from.iso()), toUtc: localMidnight(to.iso(), 1) };
      }

      async function load(): Promise<void> {
        rangeLine.text = rangeLabel();
        status.busy('Loading…');
        try {
          const { fromUtc, toUtc } = bounds();
          const summary = await crm.getReportSummary(fromUtc, toUtc, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          status.clear();
          render(summary);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the report: ' + describeError(error));
        }
      }

      async function setRange(fromIso: string, toIso: string): Promise<void> {
        from.set(fromIso || null);
        to.set(toIso || null);
        await load();
      }

      /** Called straight from the click: the new tab needs the click's user activation. */
      function exportPdf(): void {
        const { fromUtc, toUtc } = bounds();
        openInNewTab(crm.reportPdfPath(fromUtc, toUtc), 'JetsonCRM-Report.pdf').catch(error => {
          if (!ctx.cancelled()) status.error('Could not open the PDF: ' + describeError(error));
        });
      }

      async function exportCsv(): Promise<void> {
        const { fromUtc, toUtc } = bounds();
        status.busy('Preparing the CSV…');
        try {
          await download(crm.reportCsvPath(fromUtc, toUtc), 'JetsonCRM-Report.csv');
          if (!ctx.cancelled()) status.ok('CSV downloaded.');
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not download the CSV: ' + describeError(error));
        }
      }

      void load();
    }
  };
}
